import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import Layout from "./Layout";
import { useAuth } from "../hooks/useAuth";

// Gate for the agent self-service contract flow (/contract). That flow is
// anchored on the logged-in user's linked Agent record (NPN and identity), so it
// only works for "agent" accounts. Non-agent staff (admin / agency / readonly /
// submitonly) who reach it are shown a clear message instead of a dead-end form
// whose first step can't be completed without an NPN (#26).
const RequireAgent = ({ children }) => {
  const { user, loading, actingAsAgent, isStaff, signout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return <h3>Loading...</h3>;
  }

  // Not signed in: defer to the Create Account redirect, mirroring RequireAuth so
  // this gate is safe even if it's ever used without RequireAuth wrapping it.
  if (!user) {
    const dest = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signup?redirect=${dest}`} replace />;
  }

  // A plain agent, or a principal acting as their own agent (#50), may run the
  // agent flows (their identity comes from the linked Agent record).
  // #57 - a principal may also enter a flow ON BEHALF of a hierarchy agent
  // (?agent=<id>); the backend authorizes the actual agent server-side.
  const onBehalf =
    user.role === "agency" && new URLSearchParams(location.search).get("agent");
  if (actingAsAgent(user) || onBehalf) {
    return children;
  }

  const switchAccount = async () => {
    await signout();
    navigate("/login");
  };

  return (
    <Layout>
      <Box sx={{ padding: 4, display: "flex", justifyContent: "center" }}>
        <Paper sx={{ p: 4, maxWidth: 520, textAlign: "center" }} elevation={2}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h5">Agent account required</Typography>
            <Typography variant="body1" color="text.secondary">
              Contract requests are submitted by agents from their own account.
              You're signed in as {user.email}, which is not an agent account, so
              this flow is not available to you.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please sign in with an agent account to start a contract request.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={switchAccount}>
                Sign in with an agent account
              </Button>
              {isStaff(user) && (
                <Button variant="outlined" onClick={() => navigate("/admin")}>
                  Back to dashboard
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Layout>
  );
};

export default RequireAgent;
